/** @jsxImportSource @emotion/react */
/**
 * Страница детального просмотра посадки
 * URL: /plantings/:plantingId
 *
 * Тонкий orchestrator: вся логика — в хуках, весь UI — в субкомпонентах.
 * См. ./planting-details/ для деталей декомпозиции.
 */
import React, { useState } from 'react';
import { Alert, Breadcrumb, Button, Card, Descriptions, Empty, List, Modal, Space, Spin, Tag, Tooltip, Typography, message } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n, { formatDate } from '../../i18n';
import commonStyles from '../../styles/commonStyles';
import MainLayout from '../../components/common/MainLayout';
import PlantingPhotos from '../../components/plantings/PlantingPhotos';
import ActionWizard from '../../components/events/ActionWizard';
import {
  useApplyPlantingDispositionMutation,
  useUnarchivePlantingMutation,
  useGetPlantingHarvestInfoQuery,
  useResolvePlantingHarvestConflictMutation,
} from '../../api/plantingsApi';
import type {
  HarvestConflictResolutionAction,
  PlantingDispositionPayload,
  PlantingHarvestConflict,
  PlantingHarvestEvent,
  PlantingHarvestInfo,
} from '../../api/plantingsApi';
import { toastError } from '../../utils/toast';
import { usePlantingData } from './planting-details/hooks/usePlantingData';
import { useEventHistory } from './planting-details/hooks/useEventHistory';
import PlantingHeaderCard from './planting-details/components/PlantingHeaderCard';
import DiseaseAlert from './planting-details/components/DiseaseAlert';
import PropertiesCard from './planting-details/components/PropertiesCard';
import ForecastCard from './planting-details/components/ForecastCard';
import EventHistoryCard from './planting-details/components/EventHistoryCard';
import StateCorrectionsPanel from '../../components/saves/StateCorrectionsPanel';
import EditEventModal from './planting-details/components/EditEventModal';
import PlantingDispositionModal from './planting-details/components/PlantingDispositionModal';
import WeatherGapDialog from '../../components/weather/WeatherGapDialog';
import PlantingLifecycleTag from '../../features/plantings/components/PlantingLifecycleTag';
import { useRefreshWorld, useSave, useWorld } from '../../contexts/WorldContext';
import ScenarioDayControl from '../../components/scenarios/ScenarioDayControl';
import { buildScenarioPlanting, buildScenarioWorldIndexes, withPlantingRuntime } from '../../utils/scenarioWorldAdapters';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetSaveWorldQuery } from '../../api/savesV2Api';

const { Text } = Typography;

type HarvestInfoSummary = {
  totalHarvestedUnits: number;
  totalHarvestedKg: number | null;
  harvestEventCount: number;
};

const formatHarvestSummary = (info: HarvestInfoSummary): string => {
  const harvestParts: string[] = [];
  if (info.totalHarvestedUnits > 0) {
    harvestParts.push(i18n.t('plantings:harvestFacts.units', { count: info.totalHarvestedUnits }));
  }
  if (info.totalHarvestedKg != null) {
    harvestParts.push(i18n.t('plantings:harvestFacts.kg', { value: info.totalHarvestedKg }));
  }
  const eventCount = i18n.t('plantings:harvestFacts.harvestCount', { count: info.harvestEventCount });
  return harvestParts.length > 0
    ? `${harvestParts.join(', ')} · ${eventCount}`
    : eventCount;
};

const formatKg = (value: number | null | undefined): string => (
  value == null ? '—' : i18n.t('plantings:harvestFacts.kg', { value })
);

const formatUnits = (value: number | null | undefined): string => (
  value == null ? '—' : i18n.t('plantings:harvestFacts.units', { count: value })
);

const formatHarvestPolicy = (event: PlantingHarvestEvent): string => {
  if (event.quantityKgReplayPolicy === 'hard_fact') return i18n.t('plantings:harvestFacts.policy.hardFact');
  if (event.quantityKgReplayPolicy === 'replayable_model_estimate') return i18n.t('plantings:harvestFacts.policy.modelEstimate');
  if (event.quantityKgReplayPolicy === 'replayable_event_value') return i18n.t('plantings:harvestFacts.policy.eventValue');
  return '—';
};

const harvestPolicyColor = (event: PlantingHarvestEvent): string => {
  if (event.quantityKgReplayPolicy === 'hard_fact') return 'green';
  if (event.quantityKgReplayPolicy === 'replayable_model_estimate') return 'gold';
  if (event.quantityKgReplayPolicy === 'replayable_event_value') return 'blue';
  return 'default';
};

const formatProjectionStatus = (status: PlantingHarvestEvent['projectionStatus']): string => {
  if (status === 'synced') return 'projection synced';
  if (status === 'mismatch') return 'projection mismatch';
  if (status === 'projection_only') return 'projection only';
  if (status === 'event_property') return 'event fact';
  return '—';
};

const numberFromConflictData = (
  conflict: PlantingHarvestConflict,
  key: string,
): number | null => {
  const value = conflict.data?.[key];
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getConflictActions = (
  conflict: PlantingHarvestConflict,
): Array<{ action: HarvestConflictResolutionAction; label: string; danger?: boolean }> => {
  if (conflict.code === 'HARVEST_PROJECTION_MISMATCH' && conflict.eventId) {
    return [
      { action: 'sync_projection_from_event', label: i18n.t('plantings:harvestFacts.action.syncProjection') },
    ];
  }
  if (conflict.code === 'HARVEST_QUANTITY_EXCEEDS_RECONSTRUCTED_REMAINING' && conflict.eventId) {
    const remaining = numberFromConflictData(conflict, 'remainingBeforeHarvest');
    return [
      ...(remaining != null && remaining > 0
        ? [{ action: 'reduce_quantity_affected_to_remaining' as const, label: i18n.t('plantings:harvestFacts.action.reduceToRemaining') }]
        : []),
      { action: 'reject_harvest_event' as const, label: i18n.t('plantings:harvestFacts.action.reject'), danger: true },
    ];
  }
  if (conflict.code === 'HARVEST_DATE_NOT_REPLAY_COVERED' && conflict.eventId) {
    return [
      { action: 'reject_harvest_event' as const, label: i18n.t('plantings:harvestFacts.action.reject'), danger: true },
    ];
  }
  return [];
};

const HarvestFactsCard: React.FC<{
  info: PlantingHarvestInfo;
  resolvingConflict?: boolean;
  onResolveConflict?: (conflict: PlantingHarvestConflict, action: HarvestConflictResolutionAction) => void;
}> = ({ info, resolvingConflict = false, onResolveConflict }) => {
  const { t } = useTranslation('plantings');
  const conflictAlertType = info.harvestConflicts.some((conflict) => conflict.severity === 'conflict')
    ? 'error'
    : 'warning';

  return (
    <Card
      title={t('harvestFacts.title')}
      extra={
        <Space wrap size={6}>
          <Tag color={info.harvestMode === 'whole_plant' ? 'orange' : 'blue'}>
            {info.harvestMode === 'whole_plant' ? 'whole-plant' : info.harvestMode}
          </Tag>
          {info.harvestReplay.hasReplayableValues && <Tag color="gold">{t('harvestFacts.hasReplayableValues')}</Tag>}
          {info.hasHarvestConflicts && <Tag color="red">{t('harvestFacts.conflictCount', { count: info.harvestConflictCount })}</Tag>}
        </Space>
      }
    >
      <Space direction="vertical" size={12} css={commonStyles.fullWidth}>
        {info.hasHarvestConflicts && (
          <Alert
            type={conflictAlertType}
            showIcon
            message={t('harvestFacts.conflictsTitle')}
            description={
              <Space direction="vertical" size={4}>
                {info.harvestConflicts.map((conflict, index) => (
                  <Space
                    key={`${conflict.code}-${conflict.eventId || index}`}
                    direction="vertical"
                    size={4}
                    css={commonStyles.fullWidth}
                  >
                    <Text type="secondary">
                      {conflict.eventDate ? `${conflict.eventDate}: ` : ''}{conflict.message}
                    </Text>
                    {(() => {
                      // Phase 8: a harvest whose product lot is already withdrawn/reserved
                      // cannot be rejected — disable the reject action and explain why.
                      const lockedEvent = conflict.eventId
                        ? info.harvestEvents.find((e) => e.id === conflict.eventId && e.inventoryLocked)
                        : undefined;
                      const actions = getConflictActions(conflict);
                      if (!onResolveConflict || actions.length === 0) return null;
                      return (
                        <Space direction="vertical" size={2}>
                          <Space wrap size={6}>
                            {actions.map((item) => {
                              const blocked = lockedEvent && item.action === 'reject_harvest_event';
                              return (
                                <Tooltip key={item.action} title={blocked ? t('harvestFacts.rejectBlocked') : ''}>
                                  <Button
                                    size="small"
                                    danger={item.danger}
                                    disabled={!!blocked}
                                    loading={resolvingConflict}
                                    onClick={() => onResolveConflict(conflict, item.action)}
                                  >
                                    {item.label}
                                  </Button>
                                </Tooltip>
                              );
                            })}
                          </Space>
                          {lockedEvent && (
                            <Text type="warning" style={{ fontSize: 12 }}>
                              🔒 {t('harvestFacts.inventoryLocked')}
                            </Text>
                          )}
                        </Space>
                      );
                    })()}
                  </Space>
                ))}
              </Space>
            }
          />
        )}

        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 4 }}>
          <Descriptions.Item label={t('harvestFacts.harvested')}>{formatHarvestSummary(info)}</Descriptions.Item>
          <Descriptions.Item label={t('harvestFacts.mass')}>{formatKg(info.totalHarvestedKg)}</Descriptions.Item>
          <Descriptions.Item label={t('harvestFacts.remaining')}>{formatUnits(info.currentQuantity)}</Descriptions.Item>
          <Descriptions.Item label={t('harvestFacts.completed')}>{info.terminalDate ? formatDate(info.terminalDate, 'short') : '—'}</Descriptions.Item>
        </Descriptions>

        {info.harvestEvents.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('harvestFacts.noEvents')} />
        ) : (
          <List
            size="small"
            dataSource={info.harvestEvents}
            renderItem={(event) => (
              <List.Item>
                <Space direction="vertical" size={4} css={commonStyles.fullWidth}>
                  <Space wrap size={8}>
                    <Text strong>{event.eventDate ? formatDate(event.eventDate, 'short') : t('common.dateNotSet')}</Text>
                    {event.quantityAffected != null && <Tag>{formatUnits(event.quantityAffected)}</Tag>}
                    {event.quantityKg != null && <Tag color="green">{formatKg(event.quantityKg)}</Tag>}
                    <Tag color={harvestPolicyColor(event)}>{formatHarvestPolicy(event)}</Tag>
                    {event.projectionStatus && (
                      <Tag color={event.projectionStatus === 'mismatch' ? 'red' : 'default'}>
                        {formatProjectionStatus(event.projectionStatus)}
                      </Tag>
                    )}
                  </Space>
                  {event.notes && <Text type="secondary">{event.notes}</Text>}
                  {event.records.length > 0 && (
                    <Text type="secondary">
                      Projection: {event.records.map((record) => `${formatKg(record.quantityKg)}${record.saveStorageId ? ` → ${record.saveStorageId}` : ''}`).join(', ')}
                    </Text>
                  )}
                  {event.conflicts.length > 0 && (
                    <Space direction="vertical" size={2}>
                      {event.conflicts.map((conflict) => (
                        <Text key={`${event.id}-${conflict.code}`} type="danger">
                          {conflict.message}
                        </Text>
                      ))}
                    </Space>
                  )}
                </Space>
              </List.Item>
            )}
          />
        )}
      </Space>
    </Card>
  );
};

const ScenarioPlantingDetailsPage: React.FC = () => {
  const { t } = useTranslation('plantings');
  const { saveId, plantingId } = useParams<{ saveId: string; plantingId: string }>();
  const world = useWorld();
  const refreshWorld = useRefreshWorld();
  const indexes = React.useMemo(() => buildScenarioWorldIndexes(world.structure), [world.structure]);
  const rawPlanting = world.structure.plantings.find((item) => String(item.id) === String(plantingId));
  const runtimePlanting = rawPlanting ? world.runtime?.plantings?.[String(rawPlanting.id)] : null;
  const scenarioForecastSeries = React.useMemo(() => {
    if (!plantingId) return null;
    const timeSeries = (world.runtime.timeline || []).flatMap((snapshot) => {
      const point = snapshot.plantings?.[String(plantingId)];
      if (!point) return [];
      const rawPoint = point as typeof point & {
        height?: number;
        trunkDiameter?: number;
        crownDiameter?: number;
        etiolationStress?: number;
        stemThickness?: number;
        dailyGDD?: number;
        cumulativeHarvestCollectedKg?: number;
        cumulativeHarvestLostKg?: number;
      };
      const hasFlatDimensions = rawPoint.height != null
        || rawPoint.trunkDiameter != null
        || rawPoint.crownDiameter != null;
      return [{
        ...point,
        date: point.date || snapshot.simDate,
        dimensions: hasFlatDimensions
          ? {
              ...point.dimensions,
              ...(rawPoint.height != null ? { height: rawPoint.height } : {}),
              ...(rawPoint.trunkDiameter != null ? { trunkDiameter: rawPoint.trunkDiameter } : {}),
              ...(rawPoint.crownDiameter != null ? { crownDiameter: rawPoint.crownDiameter } : {}),
            }
          : point.dimensions,
        etiolation: point.etiolation || (rawPoint.etiolationStress != null
          ? {
              stress: rawPoint.etiolationStress,
              ...(rawPoint.stemThickness != null ? { stemThickness: rawPoint.stemThickness } : {}),
            }
          : undefined),
        gdd: point.gdd ?? rawPoint.dailyGDD,
        harvestCollectedCumulativeKg: point.harvestCollectedCumulativeKg
          ?? rawPoint.cumulativeHarvestCollectedKg,
        harvestLostCumulativeKg: point.harvestLostCumulativeKg
          ?? rawPoint.cumulativeHarvestLostKg,
      }];
    });
    if (timeSeries.length === 0) return null;
    const finalPoint = timeSeries[timeSeries.length - 1];
    return {
      plantingId,
      startDate: timeSeries[0].date,
      endDate: finalPoint.date,
      totalDays: timeSeries.length,
      timeSeries,
      stats: {
        totalDays: timeSeries.length,
        totalGDD: 0,
        avgStress: 0,
        maxHealthDrop: 0,
        isDead: finalPoint.isDead === true,
        deathDate: null,
        deathCause: null,
      },
      finalState: {
        ...finalPoint,
        dimensions: finalPoint.dimensions || {},
        healthScore: finalPoint.healthScore ?? runtimePlanting?.healthScore ?? 0,
        ageYears: finalPoint.ageYears ?? 0,
        vegetationStage: finalPoint.vegetationStage || runtimePlanting?.vegetationStage || 'active',
        accumulatedGDD: 0,
        isDead: finalPoint.isDead === true,
      },
    };
  }, [
    plantingId,
    runtimePlanting?.healthScore,
    runtimePlanting?.vegetationStage,
    world.runtime.timeline,
  ]);

  if (!rawPlanting) {
    return (
      <MainLayout>
        <Card><Text type="danger">{t('details.scenarioPlantingNotFound')}</Text></Card>
      </MainLayout>
    );
  }

  const planting = buildScenarioPlanting(
    withPlantingRuntime(rawPlanting, runtimePlanting || undefined),
    indexes,
  );
  const location = planting.locations[0] || null;

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb items={[
          { title: <Link to="/">{t('breadcrumb.home')}</Link> },
          { title: <Link to="/saves">{t('breadcrumb.saves')}</Link> },
          { title: <Link to={`/saves/${saveId}`}>{world.scenario?.title || t('breadcrumb.scenario')}</Link> },
          { title: <Link to={`/saves/${saveId}/plantings`}>{t('breadcrumb.plantings')}</Link> },
          { title: planting.crop?.name || t('header.plantingFallback') },
        ]} />
      }
    >
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <ScenarioDayControl saveId={saveId} />

        <Card extra={<Button onClick={() => { void refreshWorld(); }}>{t('common:actions.refresh')}</Button>}>
          <Space direction="vertical" size={8} css={commonStyles.fullWidth}>
            <Space wrap>
              <Typography.Title level={3} style={{ margin: 0 }}>
                {planting.crop?.name || t('header.plantingFallback')}
              </Typography.Title>
              <PlantingLifecycleTag status={planting.lifecycleStatus} />
              {planting.quantity && planting.quantity > 1 && (
                <Tag color="blue">{t('header.quantityTag', { count: planting.quantity })}</Tag>
              )}
            </Space>
            <Text type="secondary">Scenario save · read-only</Text>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label={t('details.bed')}>{location?.bed?.name || location?.bed?.id || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('details.plot')}>{location?.plot?.name || location?.plot?.id || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('details.field')}>{location?.field?.name || location?.field?.id || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('details.startDate')}>{planting.originDate ? formatDate(planting.originDate, 'short') : '—'}</Descriptions.Item>
            </Descriptions>
          </Space>
        </Card>

        <Card title={t('details.properties')}>
          {planting.currentProperties.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('details.noScenarioProperties')} />
          ) : (
            <Descriptions bordered size="small" column={1}>
              {planting.currentProperties.map((property) => (
                <Descriptions.Item key={String(property.propertyId)} label={property.propertyName}>
                  {property.valueNumber != null
                    ? `${property.valueNumber}${property.unit ? ` ${property.unit}` : ''}`
                    : property.valueText ?? '—'}
                </Descriptions.Item>
              ))}
            </Descriptions>
          )}
        </Card>

        <ForecastCard
          forecastSeries={scenarioForecastSeries}
          loadingForecastSeries={false}
          notPlantedDays={0}
          lifecycleTerminalInfo={null}
          harvestForecast={null}
          isForecastAnnualLike
          harvestGrossTargetKg={null}
          harvestMarketableTargetKg={null}
          plantingQuantity={planting.quantity || 1}
          showBatchQuantityMetric={false}
          initialMetric="healthScore"
        />

        <Card title={t('details.appliedEvents')}>
          {runtimePlanting?.appliedEvents && runtimePlanting.appliedEvents.length > 0 ? (
            <List
              dataSource={runtimePlanting.appliedEvents}
              renderItem={(event) => (
                <List.Item>
                  <Space direction="vertical" size={0}>
                    <Text strong>{event.action || t('eventName.generic')}</Text>
                    <Text type="secondary">{event.date ? formatDate(event.date, 'short') : t('common.dateNotSet')}</Text>
                  </Space>
                </List.Item>
              )}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('details.noAppliedEvents')} />
          )}
        </Card>
      </Space>
    </MainLayout>
  );
};

const FarmBoundPlantingDetailsPage: React.FC = () => {
  const { t } = useTranslation('plantings');
  const { saveId } = useParams<{ saveId: string }>();
  // Дата курсора сейва для формы коррекций (не календарное «сегодня»).
  const { data: saveWorldForCursor } = useGetSaveWorldQuery(saveId || skipToken);
  const [actionWizardOpen, setActionWizardOpen] = useState(false);
  const [dispositionOpen, setDispositionOpen] = useState(false);
  const [weatherGapDismissed, setWeatherGapDismissed] = useState(false);
  const data = usePlantingData();
  const history = useEventHistory(data.plantingId, data.reloadPlantingDerivedData);
  const [applyDisposition, { isLoading: applyingDisposition }] = useApplyPlantingDispositionMutation();
  const [unarchivePlanting, { isLoading: unarchiving }] = useUnarchivePlantingMutation();
  const [resolveHarvestConflict, { isLoading: resolvingHarvestConflict }] = useResolvePlantingHarvestConflictMutation();
  const { data: harvestInfo, refetch: refetchHarvestInfo } = useGetPlantingHarvestInfoQuery(
    data.plantingId ? { plantingId: String(data.plantingId) } : skipToken,
  );

  if (data.isLoading) {
    return <MainLayout><Card><Spin /></Card></MainLayout>;
  }

  if (data.isError || !data.planting) {
    return (
      <MainLayout>
        <Card><Text type="danger">{t('details.loadFailed')}</Text></Card>
      </MainLayout>
    );
  }

  const { planting, resolvedFarmId, simSessionId, simSaveId, simDisplayDate, activeLocation } = data;

  const handleDispositionSubmit = async (payload: PlantingDispositionPayload) => {
    try {
      await applyDisposition({ saveId: saveId || '', id: planting.id, payload }).unwrap();
      await data.reloadPlantingDerivedData();
      await history.reloadHistoryIfNeeded();
      setDispositionOpen(false);
      message.success(t('disposition.applied'));
    } catch (err) {
      toastError(err);
    }
  };

  const handleActionSuccess = async () => {
    await data.reloadPlantingDerivedData();
    await history.reloadHistoryIfNeeded();
    if (data.plantingId) {
      await refetchHarvestInfo();
    }
  };

  const isArchived = planting.lifecycleStatus === 'archived';
  const hasHarvestFacts = harvestInfo
    ? harvestInfo.totalHarvestedUnits > 0
      || harvestInfo.totalHarvestedKg != null
      || harvestInfo.harvestEventCount > 0
    : false;
  // The backend archives a planting when a harvest takes its last plant; the
  // status reason is free text, so the signal is the harvest facts, not the text.
  const archivedByHarvest = isArchived
    && (planting.statusReason === 'harvest_completed'
      || (hasHarvestFacts && Number(harvestInfo?.currentQuantity ?? planting.quantity ?? 0) === 0));
  const showHarvestFactsCard = !!harvestInfo && (hasHarvestFacts || harvestInfo.hasHarvestConflicts);
  const harvestSummary = harvestInfo ? formatHarvestSummary(harvestInfo) : null;

  const handleUnarchive = () => {
    Modal.confirm({
      title: t('unarchive.confirmTitle'),
      content: t('unarchive.confirmContent'),
      okText: t('unarchive.restore'),
      cancelText: t('common:actions.cancel'),
      onOk: async () => {
        try {
          await unarchivePlanting({ plantingId: String(planting.id) }).unwrap();
          await data.reloadPlantingDerivedData();
          message.success(t('unarchive.restored'));
        } catch (err) {
          toastError(err);
        }
      },
    });
  };

  const handleResolveHarvestConflict = async (
    conflict: PlantingHarvestConflict,
    action: HarvestConflictResolutionAction,
  ) => {
    const quantityAffected = action === 'reduce_quantity_affected_to_remaining'
      ? numberFromConflictData(conflict, 'remainingBeforeHarvest')
      : null;
    try {
      await resolveHarvestConflict({
        plantingId: String(planting.id),
        payload: {
          conflictCode: conflict.code,
          action,
          eventId: conflict.eventId || undefined,
          quantityAffected,
        },
      }).unwrap();
      await data.reloadPlantingDerivedData();
      await history.reloadHistoryIfNeeded();
      message.success(t('harvestFacts.conflictResolved'));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb items={[
          { title: <Link to="/">{t('breadcrumb.home')}</Link> },
          { title: <Link to="/saves">{t('breadcrumb.saves')}</Link> },
          { title: <Link to={`/saves/${saveId}`}>{data.farmName || t('breadcrumb.save')}</Link> },
          { title: <Link to={`/saves/${saveId}/plantings`}>{t('breadcrumb.plantings')}</Link> },
          { title: planting.crop?.name || t('header.plantingFallback') },
        ]} />
      }
    >
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        {isArchived && (
          <Alert
            type="warning"
            showIcon
            message={archivedByHarvest
              ? t('archived.byHarvest')
              : (planting.statusReason ? t('archived.withReason', { reason: planting.statusReason }) : t('archived.title'))}
            description={hasHarvestFacts && harvestInfo && harvestSummary
              ? (harvestInfo.terminalDate
                ? t('archived.harvestedCompleted', { summary: harvestSummary, date: formatDate(harvestInfo.terminalDate, 'short') })
                : t('archived.harvested', { summary: harvestSummary }))
              : t('archived.bedFreed')}
            action={
              <Button size="small" loading={unarchiving} onClick={handleUnarchive}>
                {t('unarchive.restore')}
              </Button>
            }
          />
        )}

        {showHarvestFactsCard && harvestInfo && (
          <HarvestFactsCard
            info={harvestInfo}
            resolvingConflict={resolvingHarvestConflict}
            onResolveConflict={handleResolveHarvestConflict}
          />
        )}

        {/* Заголовок + основная информация */}
        <Card>
          <PlantingHeaderCard
            planting={planting}
            currentLocation={data.currentLocation}
            onActionClick={() => setActionWizardOpen(true)}
            onDispositionClick={() => setDispositionOpen(true)}
            projectedQuantity={data.projectedQuantity}
          />
        </Card>

        {/* Болезни/вредители (симуляция) */}
        {simSessionId && <DiseaseAlert forecastSeries={data.forecastSeries} />}

        {/* Сведения (Properties) */}
        <PropertiesCard
          simSessionId={simSessionId}
          simDisplayDate={simDisplayDate}
          loadingAttributes={data.loadingAttributes}
          displayedAttributes={data.displayedAttributes}
          simulatedAgeDisplayUnit={data.simulatedAgeDisplayUnit}
          simulatedAgeDisplayValue={data.simulatedAgeDisplayValue}
          simulatedAgeDisplayDateLabel={data.simulatedAgeDisplayDateLabel}
          bbch={data.forecastSeries?.finalState?.bbch ?? null}
        />

        {/* Прогнозные графики симуляции */}
        {simSessionId && (
          <ForecastCard
            forecastSeries={data.forecastSeries}
            loadingForecastSeries={data.loadingForecastSeries}
            notPlantedDays={data.notPlantedDays}
            lifecycleTerminalInfo={data.lifecycleTerminalInfo}
            harvestForecast={data.harvestForecast}
            isForecastAnnualLike={data.isForecastAnnualLike}
            harvestGrossTargetKg={data.harvestGrossTargetKg}
            harvestMarketableTargetKg={data.harvestMarketableTargetKg}
            plantingQuantity={data.plantingQuantity}
            showBatchQuantityMetric={data.showBatchQuantityMetric}
          />
        )}

        {/* История событий */}
        <EventHistoryCard
          historyLoaded={history.historyLoaded}
          loadingEvents={history.loadingEvents}
          fetchingEvents={history.fetchingEvents}
          events={history.events}
          propertyHistory={history.propertyHistory}
          plantingQuantity={planting.quantity}
          initialPlantingQuantity={planting.initialQuantity}
          plantingLocations={planting.locations}
          onLoadHistory={history.handleLoadHistory}
          onEditEvent={history.handleOpenEditEvent}
          onDeleteEvent={history.handleDeleteEvent}
          isEventLocked={history.isEventLocked}
        />

        {/* Коррекции показателей (state_correction): посадка + её грядка */}
        {saveId && (
          <StateCorrectionsPanel
            saveId={saveId}
            cursorDate={saveWorldForCursor?.cursor?.simDate || null}
            listFilters={[
              { plantingId: String(planting.id) },
              ...(activeLocation?.bed?.id ? [{ bedId: String(activeLocation.bed.id) }] : []),
            ]}
            targets={[
              { kind: 'planting', id: String(planting.id), label: planting.crop?.name ? String(planting.crop.name) : t('details.thisPlanting') },
              ...(activeLocation?.bed?.id
                ? [{ kind: 'bed' as const, id: String(activeLocation.bed.id), label: activeLocation.bed.name ? String(activeLocation.bed.name) : t('details.plantingBed') }]
                : []),
            ]}
          />
        )}

        {/* Фотографии */}
        <Card title={t('details.photos')}>
          <PlantingPhotos
            saveId={saveId || ''}
            plantingId={planting.id}
            remotePhotos={planting.photos}
            readonly={false}
          />
        </Card>
      </Space>

      {/* Модалка корректировки события */}
      <EditEventModal
        open={history.editEventOpen}
        editingEvent={history.editingEvent}
        form={history.editEventForm}
        saving={history.savingEvent}
        onSave={history.handleSaveEventCorrection}
        onCancel={history.closeEditModal}
        dateLocked={history.editingEventLocked}
        openDay={history.openDay}
      />

      {/* Wizard создания событий */}
      <ActionWizard
        targets={[{
          plantingId: String(planting.id),
          bedId: activeLocation?.bed?.id ? String(activeLocation.bed.id) : undefined,
          plotId: activeLocation?.plot?.id ? String(activeLocation.plot.id) : undefined,
          fieldId: activeLocation?.field?.id ? String(activeLocation.field.id) : undefined,
        }]}
        farmId={resolvedFarmId || ''}
        cropId={planting.crop?.id ? Number(planting.crop.id) : null}
        plantingType={planting.plantingType}
        plantingQuantity={planting.quantity}
        open={actionWizardOpen}
        onClose={() => setActionWizardOpen(false)}
        onSuccess={handleActionSuccess}
        contextLabel={planting.crop?.name || t('header.plantingFallback')}
        simSessionId={simSessionId || undefined}
        simSaveId={simSaveId || undefined}
        simDisplayDate={simDisplayDate || undefined}
      />

      <PlantingDispositionModal
        open={dispositionOpen}
        planting={planting}
        loading={applyingDisposition}
        onCancel={() => setDispositionOpen(false)}
        onSubmit={handleDispositionSubmit}
        cursorDate={saveWorldForCursor?.cursor?.simDate || null}
      />

      <WeatherGapDialog
        open={Boolean(data.forecastWeatherGap) && !weatherGapDismissed}
        gap={data.forecastWeatherGap}
        onClose={() => setWeatherGapDismissed(true)}
        onExtended={() => {
          setWeatherGapDismissed(false);
          data.refetchForecastSeries?.();
        }}
      />
    </MainLayout>
  );
};

const PlantingDetailsPage: React.FC = () => {
  const currentSave = useSave();
  if (currentSave.type === 'scenario') {
    return <ScenarioPlantingDetailsPage />;
  }
  return <FarmBoundPlantingDetailsPage />;
};

export default PlantingDetailsPage;
